import React, { useRef, useState, useEffect, useMemo } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { cloneDeep } from 'lodash-es';
import moment from 'moment';
import Filter from '@/pages/intelligence/components/Filter';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import { feachTimeTypeToFilterTimeType } from '@/pages/intelligence/utils/common';
import { getAllCountries } from '@/pages/intelligence/utils/regionSelectUtils';
import { getInitialInputList, getInitialFormValue } from './utils';
import {
  // 日期筛选器
  RANGE_TIME as RANGE_TIME_SELECT_FILTER,
  // 组合国家筛选器
  COMPOSED_COUNTRY_SELECT as COMPOSED_COUNTRY_SELECT_FILTER,
} from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { DEFAULT_FORM_VALUE, FILTER_DEFAULT_INPUT_LIST } from './config';

import type { IBaseProps, IDateTypely, IRegion } from '@/pages/intelligence/types/common';
import type { IPCStoreRankTopFilter } from '@/pages/intelligence/types/storeRank';
import type { ICascaderSelectOverallGroup } from '@home/components/CascaderSelect';

interface IPCStoreRankTopFilterProps extends IBaseProps {
  // 筛选器右侧的info提示
  iconText?: React.ReactNode;
  isOperate: boolean;
  typeList?: ('date' | 'week' | 'month')[];
  granularity?: IDateTypely;
  startDate?: string;
  endDate?: string;
  isRegionVisible?: boolean;
  regionOptions?: IRegion[] | null;
  overallGroup?: ICascaderSelectOverallGroup;
  defaultRegions?: string[];
  multiCountryText?: React.ReactNode;
  onClickMultiCountry?: () => void;
  onConfirm?: (payload: Partial<IPCStoreRankTopFilter>) => void;
  onReset?: () => void;
}

const PCStoreRankTopFilter: React.FC<IPCStoreRankTopFilterProps> = (props) => {
  const {
    style,
    className,
    isOperate,
    iconText,
    typeList = ['date'],
    granularity,
    startDate,
    endDate,
    isRegionVisible = false,
    regionOptions,
    overallGroup,
    defaultRegions,
    multiCountryText,
    onClickMultiCountry,
    onConfirm,
    onReset,
  } = props;

  const { formatMessage } = useIntl();

  /**
   * 继承逻辑在这写
   * formValue改成用ref，可提供给父组件回填时间， 或添加继承逻辑提供formValue给父组件
   */
  const { filterExtend, setFilterExtend, resetFilterExtend } = useFilterExtend({});

  // 筛选器初始配置
  const initialInputList = getInitialInputList(FILTER_DEFAULT_INPUT_LIST, {
    typeList,
    composedCountryVisible: isRegionVisible,
  });

  const filterDefaultValue = useMemo(() => {
    const countries = Array.isArray(defaultRegions) && defaultRegions.length > 0
      ? defaultRegions
      : DEFAULT_FORM_VALUE[COMPOSED_COUNTRY_SELECT_FILTER].value.countries;

    return {
      ...DEFAULT_FORM_VALUE,
      [COMPOSED_COUNTRY_SELECT_FILTER]: {
        value: {
          ...DEFAULT_FORM_VALUE[COMPOSED_COUNTRY_SELECT_FILTER].value,
          countries,
        },
      },
    };
  }, [defaultRegions]);

  // 筛选器的初始值
  const initialFormValue = getInitialFormValue(isOperate, filterExtend, filterDefaultValue, { typeList });

  // 筛选器UI渲染需要的value
  const formValue = useRef<any>(initialFormValue);

  const [inputList, setInputList] = useState(initialInputList);

  const updateComposedCountryCfg = (
    list: any[],
    cfg: {
      visible: boolean;
      options?: IRegion[] | null;
      overallGroup?: ICascaderSelectOverallGroup;
      multiCountryText?: React.ReactNode;
      onClickMultiCountry?: () => void;
    },
  ) => {
    if (!Array.isArray(list)) return;
    const {
      visible,
      options,
      overallGroup: overallGroupTmp,
      multiCountryText: multiText,
      onClickMultiCountry: onMultiCountry,
    } = cfg || {};
    const composedCountryFilterCfg = list.find(
      (item) => item?.key === COMPOSED_COUNTRY_SELECT_FILTER,
    );
    if (!composedCountryFilterCfg) return;
    composedCountryFilterCfg.visible = visible ? 1 : 2;
    composedCountryFilterCfg.options = Array.isArray(options) ? options : [];
    composedCountryFilterCfg.overallGroup = overallGroupTmp;
    composedCountryFilterCfg.multiCountryText = multiText;
    composedCountryFilterCfg.onClickMultiCountry = onMultiCountry;
    composedCountryFilterCfg.overlayClassName = 'dbi-alltopcharts-region-cascader-select-overlay';
    composedCountryFilterCfg.hoverTip = formatMessage({
      id: 'dbi_store_rank_region_filter_top_sellers_only',
    });
  };

  useEffect(() => {
    const inputListTmp = cloneDeep(inputList);
    updateComposedCountryCfg(inputListTmp, {
      visible: isRegionVisible,
      options: regionOptions,
      overallGroup,
      multiCountryText,
      onClickMultiCountry,
    });
    setInputList(inputListTmp);
  }, [
    isRegionVisible,
    regionOptions,
    overallGroup,
    multiCountryText,
    onClickMultiCountry,
    formatMessage,
  ]);

  useEffect(() => {
    if (!isRegionVisible) return;
    if (!regionOptions?.length) return;
    const allCountries = [
      ...getAllCountries(regionOptions),
      ...(overallGroup?.options ?? []).map((option) => option?.value).filter(Boolean),
    ];
    const composedCountryFormValue = formValue.current?.[COMPOSED_COUNTRY_SELECT_FILTER]?.value;
    const composedCountries = composedCountryFormValue?.countries;
    const isComposedCountriesValid = allCountries?.includes(composedCountries?.[0]);
    if (isComposedCountriesValid) return;
    formValue.current = {
      ...formValue.current,
      [COMPOSED_COUNTRY_SELECT_FILTER]: {
        value: {
          ...formValue.current[COMPOSED_COUNTRY_SELECT_FILTER].value,
          countries: filterDefaultValue[COMPOSED_COUNTRY_SELECT_FILTER].value.countries,
        },
      },
    };
  }, [isRegionVisible, regionOptions, overallGroup, filterDefaultValue]);

  /**
   * 同步父组件传入的时间
   */
  useEffect(() => {
    const rangeTimeValue = formValue.current?.[RANGE_TIME_SELECT_FILTER];
    formValue.current = {
      ...formValue.current,
      [RANGE_TIME_SELECT_FILTER]: {
        type: granularity ? feachTimeTypeToFilterTimeType(granularity) : rangeTimeValue?.type ?? 'date',
        value: startDate && endDate ? [moment(startDate), moment(endDate)] : rangeTimeValue?.value ?? [],
      },
    };
  }, [granularity, startDate, endDate]);

  const inputListTmp = useMemo(() => {
    return inputList.map(item => {
      if (item?.key === RANGE_TIME_SELECT_FILTER) {
        item.showRangePicker = (formValue.current?.[RANGE_TIME_SELECT_FILTER]?.type !== 'date');
        return item;
      }
      return item;
    });
  }, [inputList, formValue.current]);

  /**
   * 筛选器confirm的回调
   * @param value 筛选器数据（筛选器中各个组件UI渲染需要的value）
   * @param apiValue 处理后的筛选器数据，为了更方便调用接口，针对高频特殊筛选器的数据做了数据格式转换（将筛选器原始的数据转换为更方便接口入参的数据格式，同时也会保留筛选器最原始的数据，用于筛选器继承）
   */
  const handleConfirm = (value: any, apiValue: any) => {
    formValue.current = value;
    const rangeTime = apiValue?.[RANGE_TIME_SELECT_FILTER];
    const composedCountry = apiValue?.[COMPOSED_COUNTRY_SELECT_FILTER]?.value;
    const composedCountries = composedCountry?.countries;
    const countries = composedCountries?.length === 1
      ? composedCountries
      : filterDefaultValue[COMPOSED_COUNTRY_SELECT_FILTER].value.countries;

    const filterTmp = {
      region_type: 'market',
      regions: isRegionVisible ? countries : ['global'],
      granularity: rangeTime?.granularity ?? 'daily',
      start_date: rangeTime?.start_date ?? '',
      end_date: rangeTime?.end_date ?? '',
    };
    onConfirm?.(filterTmp);
    setFilterExtend(apiValue);
  };

  const handleReset = () => {
    formValue.current = {
      ...filterDefaultValue,
      [RANGE_TIME_SELECT_FILTER]: {
        type: 'date',
        value: [moment(), moment()],
      },
    };
    resetFilterExtend();
    onReset?.();
  };

  return (
    <Filter
      style={style}
      className={classnames('dbi-store-rank-top-filter dbi-pc-store-rank-top-filter', className)}
      iconText={iconText}
      showButton={false}
      inputList={inputListTmp}
      defaultValue={filterDefaultValue}
      value={formValue.current}
      onConfirm={handleConfirm}
      onReset={handleReset}
    />
  );
};

export default PCStoreRankTopFilter;
